import { FIELD_NAMES } from '@/pages/inventory/inventoryPriceStatus/constants'
import { InventoryPriceStatusFormValues } from '@/pages/inventory/inventoryPriceStatus/filterToRequest'

/**
 * 메뉴 세션·URL 쿼리에 싣는 검색 조건 모양. (MOMS 공통 UX — 다른 메뉴를 다녀와도 마지막 조회 조건이 남는다)
 *
 * 필드 이름은 폼 값 키를 그대로 써서 URL 만 봐도 뜻이 통하게 둔다(ST10 동일).
 * 브랜드명·바코드명은 다건이라 배열로 싣는다 — `Utils.qsStringify` 가 콤마로 직렬화하고
 * `useParsedQuery` 가 `comma: true` 로 되돌린다.
 */
export interface InventoryPriceStatusSearchQuery {
  shipperCode?: string
  brandName?: string[]
  goodsCode?: string
  barcodes?: string[]
}

/** 값이 있는 항목만 남긴다 — 빈 값은 키 자체를 빼 쿼리스트링을 짧게 유지한다. */
const filled = (values?: string[]) => (values ?? []).map((v) => v.trim()).filter(Boolean)

/** 세션·URL 에 저장할 형태로 만든다. 브랜드 '전체'(빈 문자열)는 필터가 아니라 자연히 빠진다. */
export const toSearchQuery = (values: InventoryPriceStatusFormValues): InventoryPriceStatusSearchQuery => {
  const shipperCode = values[FIELD_NAMES.shipperCode]?.trim()
  const goodsCode = values[FIELD_NAMES.goodsCode]?.trim()
  const brandName = filled(values[FIELD_NAMES.brandName])
  const barcodes = filled(values[FIELD_NAMES.barcodes])

  return {
    ...(shipperCode ? { shipperCode } : {}),
    ...(brandName.length > 0 ? { brandName } : {}),
    ...(goodsCode ? { goodsCode } : {}),
    ...(barcodes.length > 0 ? { barcodes } : {}),
  }
}

/** 문자열이 아닌 값(조작된 URL)은 버린다 — 폼이 깨지지 않게 한다. */
const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)

/** 단건이면 qs 가 문자열로 돌려주므로 배열로 맞춘다. */
const textList = (value: unknown): string[] | undefined => {
  const list = Array.isArray(value) ? value.map(text).filter(Boolean) : [text(value)].filter(Boolean)
  return list.length > 0 ? (list as string[]) : undefined
}

/**
 * 저장된 쿼리를 폼 값으로 되돌린다. 쿼리에 없는 항목은 화면 기본값(프로필 화주·브랜드 전체)을 쓴다.
 */
export const fromSearchQuery = (
  query: Partial<InventoryPriceStatusSearchQuery>,
  defaults: InventoryPriceStatusFormValues,
): InventoryPriceStatusFormValues => ({
  [FIELD_NAMES.shipperCode]: text(query.shipperCode) ?? defaults[FIELD_NAMES.shipperCode],
  [FIELD_NAMES.brandName]: textList(query.brandName) ?? defaults[FIELD_NAMES.brandName],
  [FIELD_NAMES.goodsCode]: text(query.goodsCode),
  [FIELD_NAMES.barcodes]: textList(query.barcodes),
})
